import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { api, formatApiError, inr } from "../lib/api";
import { SectionHead } from "../components/Motion";

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("id") || "");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e?.preventDefault();
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/track/${orderId}?email=${encodeURIComponent(email)}`);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="pt-28 pb-24 max-w-2xl mx-auto px-4 sm:px-6 min-h-screen" data-testid="track-order-page">
      <SectionHead overline="Order Tracking" title="Where Is My Gift?" center testid="track-head" />
      <form onSubmit={track} className="card-surface rounded-2xl p-6 space-y-4" data-testid="order-tracking-modal">
        <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Order ID (e.g. SAV-2026-000001)" required
          className="w-full bg-[#FFFFFF] border border-[#7E1E3F]/25 rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]"
          data-testid="order-search-input" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used at checkout" required
          className="w-full bg-[#FFFFFF] border border-[#7E1E3F]/25 rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]"
          data-testid="track-email-input" />
        <button disabled={loading} className="w-full bg-[#7E1E3F] text-[#FFFFFF] text-xs font-semibold tracking-wider uppercase rounded-full py-3.5 hover:bg-[#5C1230] transition-colors disabled:opacity-60" data-testid="track-submit">
          {loading ? "Locating…" : "Track Order"}
        </button>
        {error && <p className="text-sm text-[#E63946]" data-testid="track-error">{error}</p>}
      </form>

      {result && (
        <div className="card-surface rounded-2xl p-6 mt-8" data-testid="track-result">
          <div className="flex justify-between items-baseline flex-wrap gap-2 mb-6">
            <p className="font-mono-lux text-[#7E1E3F]">{result.order.order_id}</p>
            <p className="text-sm text-[#857078]">Est. delivery: {result.order.estimated_delivery}</p>
          </div>
          <div className="space-y-0">
            {result.statuses.map((s, i) => {
              const done = result.order.timeline.some((t) => t.status === s);
              const at = result.order.timeline.find((t) => t.status === s)?.at;
              const current = result.order.status === s;
              return (
                <div key={s} className="flex gap-4" data-testid={`track-step-${i}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${done ? "bg-[#7E1E3F] border-[#7E1E3F] text-[#FFFFFF]" : "border-[#7E1E3F]/25 text-transparent"}`}>
                      <Check size={13} />
                    </div>
                    {i < result.statuses.length - 1 && <div className={`w-px h-8 ${done ? "bg-[#7E1E3F]/60" : "bg-[#7E1E3F]/15"}`} />}
                  </div>
                  <div className="pb-6">
                    <p className={`text-sm ${done ? "text-[#2E1A22]" : "text-[#857078]"} ${current ? "font-semibold" : ""}`}>{s}</p>
                    {at && <p className="text-[11px] text-[#857078]">{new Date(at).toLocaleString("en-IN")}</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-[#7E1E3F]/10 pt-4 text-sm text-[#857078] flex justify-between">
            <span>{result.order.items.map((i) => `${i.name} × ${i.qty}${i.personalization ? ` (for “${i.personalization.name}”)` : ""}`).join(", ")}</span>
            <span className="text-[#2E1A22] font-semibold">{inr(result.order.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
